import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StatusAccountEnum } from '../utils/StatusAccountEnum';

// Enum para níveis de usuário
export enum UserLevel {

    NONE = 0,
    
    /// <summary>
    /// Super Admin. Nível mais alto (apenas para usuários internos).
    /// </summary>
    SUPER_ADMIN_USER = 1,

    /// <summary>
    /// Admin do Cliente. Nível com permissões administrativas dentro do cliente.
    /// </summary>
    ADMIN_USER = 2,

    /// <summary>
    /// Representa um usuário comum do sistema.
    /// </summary>
    GENERAL_USER = 3
}

interface User {
  id: string;
  email: string;
  name: string;
  userLevel: UserLevel;
  companyName?: string;
  customerGuid?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isSuperUser: boolean;
  isAdminUser: boolean;
  login: (email: string, password: string) => Promise<import('../utils/apiSchemas').ApiSuccessResponse<import('../pages/Login/login.api').AuthenticationResponseData>>;
  loginByPassSubscription: (email: string, authenticationGuid: string) => Promise<import('../utils/apiSchemas').ApiSuccessResponse<import('../pages/Login/login.api').AuthenticationResponseData>>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Chaves para localStorage
const AUTH_STORAGE_KEY = 'wg_tech_auth_user';
const AUTH_TIMESTAMP_KEY = 'wg_tech_auth_timestamp';
const AUTH_TOKEN_KEY = 'wg_tech_access_token';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null);

  // Função para salvar dados de autenticação
  const saveAuthData = (userData: User) => {
    try {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
      localStorage.setItem(AUTH_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Erro ao salvar dados de autenticação:', error);
    }
  };

  // Função para limpar dados de autenticação
  const clearAuthData = () => {
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      localStorage.removeItem(AUTH_TIMESTAMP_KEY);
      localStorage.removeItem(AUTH_TOKEN_KEY);
    } catch (error) {
      console.error('Erro ao limpar dados de autenticação:', error);
    }
  };

  // Função para verificar se a sessão ainda é válida
  const isSessionValid = (): boolean => {
    try {
      const timestamp = localStorage.getItem(AUTH_TIMESTAMP_KEY);
      if (!timestamp) return false;

      const sessionTime = parseInt(timestamp);
      const currentTime = Date.now();
      
      // Sessão válida por 30 dias (pode ser configurado)
      const SESSION_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 dias em millisegundos
      
      return (currentTime - sessionTime) < SESSION_DURATION;
    } catch (error) {
      console.error('Erro ao verificar validade da sessão:', error);
      return false;
    }
  };

  // Função para carregar dados salvos
  const loadSavedAuth = () => {
    try {
      const savedUser = localStorage.getItem(AUTH_STORAGE_KEY);
      
      if (savedUser && isSessionValid()) {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        // Atualizar timestamp para renovar a sessão
        localStorage.setItem(AUTH_TIMESTAMP_KEY, Date.now().toString());
      } else {
        // Limpar dados expirados
        clearAuthData();
      }
    } catch (error) {
      console.error('Erro ao carregar dados de autenticação:', error);
      clearAuthData();
    } finally {
      // Usar setTimeout para evitar flicker durante navegação
      setTimeout(() => setIsLoading(false), 0);
    }
  };

  const login = async (email: string, password: string): Promise<import('../utils/apiSchemas').ApiSuccessResponse<import('../pages/Login/login.api').AuthenticationResponseData>> => {
    try {
      const { authenticateUser } = await import('../pages/Login/login.api');

      const res = await authenticateUser({ emailAddress: email, password });
      if (res?.success && res?.data) {
        const { accessToken, userAccessGuid, userLevel, statusAccount, customerGuid, isTrialing, trialDaysRemaining } = res.data;

        // Only authenticate/store if account is ACTIVE (1)
        if (statusAccount === StatusAccountEnum.ACTIVE) {
          const mapUserLevel = (lvl: number): UserLevel => {
            switch (lvl) {
              case 1: return UserLevel.SUPER_ADMIN_USER;
              case 2: return UserLevel.ADMIN_USER;
              case 3: return UserLevel.GENERAL_USER;
              default: return UserLevel.GENERAL_USER;
            }
          };

          const userData: User = {
            id: userAccessGuid,
            email,
            name: email,
            userLevel: mapUserLevel(userLevel),
            customerGuid: customerGuid
          };

          setUser(userData);
          saveAuthData(userData);
          try {
            localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
          } catch (e) {
            console.error('Erro ao salvar accessToken:', e);
          }

          if (isTrialing === true) {
            const days = typeof trialDaysRemaining === 'number' ? trialDaysRemaining : 0;
            setTrialDaysRemaining(days);
          }
        }
      }
      return res;
    } catch (error) {
      console.error('Erro durante o login:', error);
      throw error;
    }
  };

  const loginByPassSubscription = async (
    email: string,
    authenticationGuid: string
  ): Promise<import('../utils/apiSchemas').ApiSuccessResponse<import('../pages/Login/login.api').AuthenticationResponseData>> => {
    try {
      const { authenticateUserByPassSubscription } = await import('../pages/Login/login.api');

      const res = await authenticateUserByPassSubscription({ emailAddress: email, authenticationGuid });
      if (res?.success && res?.data) {
        const { accessToken, userAccessGuid, userLevel, statusAccount, customerGuid, isTrialing, trialDaysRemaining } = res.data;

        if (statusAccount === StatusAccountEnum.ACTIVE) {
          const mapUserLevel = (lvl: number): UserLevel => {
            switch (lvl) {
              case 1: return UserLevel.SUPER_ADMIN_USER;
              case 2: return UserLevel.ADMIN_USER;
              case 3: return UserLevel.GENERAL_USER;
              default: return UserLevel.GENERAL_USER;
            }
          };

          const userData: User = {
            id: userAccessGuid,
            email,
            name: email,
            userLevel: mapUserLevel(userLevel),
            customerGuid: customerGuid
          };

          setUser(userData);
          saveAuthData(userData);
          try {
            localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
          } catch (e) {
            console.error('Erro ao salvar accessToken:', e);
          }

          if (isTrialing === true) {
            const days = typeof trialDaysRemaining === 'number' ? trialDaysRemaining : 0;
            setTrialDaysRemaining(days);
          }
        }
      }

      return res;
    } catch (error) {
      console.error('Erro durante o login via bypass:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    clearAuthData();
  };

  // Verificar autenticação salva na inicialização
  useEffect(() => {
    loadSavedAuth();
  }, []);

  // Verificar periodicamente se a sessão ainda é válida
  useEffect(() => {
    if (user) {
      const interval = setInterval(() => {
        if (!isSessionValid()) {
          logout();
        }
      }, 60000); // Verificar a cada minuto

      return () => clearInterval(interval);
    }
  }, [user]);

  const value = {
    user,
    isAuthenticated: !!user,
    isSuperUser: user?.userLevel === UserLevel.SUPER_ADMIN_USER,
    isAdminUser: user?.userLevel === UserLevel.ADMIN_USER,
    login,
    loginByPassSubscription,
    logout,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      <Dialog
        open={trialDaysRemaining !== null}
        onClose={() => setTrialDaysRemaining(null)}
        aria-labelledby="trial-modal-title"
      >
        <DialogTitle id="trial-modal-title">{t('trial_modal_title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('trial_modal_message', { days: trialDaysRemaining ?? 0 })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrialDaysRemaining(null)} variant="contained">
            {t('trial_modal_close')}
          </Button>
        </DialogActions>
      </Dialog>
      {children}
    </AuthContext.Provider>
  );
};
